/**
 * Create Override Modal - Add manual schedule override for a specific date
 */

import { useQueryClient } from '@tanstack/react-query'
import React, { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Button, Dialog, FormItem } from 'vnetwork-ui'

import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { getEmployeesOptions } from '@/modules/Employee/services'
import { ONCALL_QUERY_KEYS } from '@/modules/Oncall/constants'
import { createOverride } from '@/modules/Oncall/services'
import { Rotation } from '@/modules/Oncall/types/rotation'
import dayjs from 'dayjs'

interface FormData {
    rotation_id: number
    slot_index: number
    employee_id: number
    reason: string
}

export interface CreateOverrideModalProps {
    open: boolean
    onClose: () => void
    scheduleId: number
    date: string
    rotations?: Rotation[]
    selectedRotationId?: number
    onSuccess?: () => void
}

const DAYS_OF_WEEK = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat']

const CreateOverrideModal: React.FC<CreateOverrideModalProps> = ({
    open,
    onClose,
    scheduleId,
    date,
    rotations = [],
    selectedRotationId,
    onSuccess,
}) => {
    const { t } = useTranslation('oncall')
    const queryClient = useQueryClient()
    const [employeeOptions, setEmployeeOptions] = useState<
        { label: string; value: number }[]
    >([])

    // Build rotation options
    const rotationOptions = rotations.map((r) => ({
        label: r.oncall_rotation_name,
        value: r.oncall_rotation_id,
    }))

    const {
        control,
        handleSubmit,
        reset,
        watch,
        formState: { isSubmitting },
    } = useForm<FormData>({
        mode: 'onSubmit',
        defaultValues: {
            rotation_id:
                selectedRotationId || rotations[0]?.oncall_rotation_id || 0,
            slot_index: 0,
            employee_id: 0,
            reason: '',
        },
    })

    const watchRotationId = watch('rotation_id')

    // Get selected rotation and its available slots for the date
    const selectedRotation = useMemo(
        () => rotations.find((r) => r.oncall_rotation_id === watchRotationId),
        [rotations, watchRotationId]
    )

    // Get available time slots for the selected date
    const { availableSlots, hasNoSlotsForDate } = useMemo(() => {
        // Default full day option as fallback
        const fullDayOption = {
            label: t('all_day', 'All Day (00:00 - 24:00)'),
            value: 0,
            time_from: '00:00',
            time_to: '24:00',
        }

        if (!selectedRotation?.timing) {
            return { availableSlots: [fullDayOption], hasNoSlotsForDate: false }
        }

        const dayOfWeek = dayjs(date).day()
        const timing = selectedRotation.timing

        if (timing.timing_type === 'everyday') {
            return { availableSlots: [fullDayOption], hasNoSlotsForDate: false }
        }

        if (
            timing.timing_type === 'custom_days' &&
            timing.time_slots?.length > 0
        ) {
            const slots = timing.time_slots
                .map((slot, index) => {
                    // Check if this slot covers the selected day
                    const dayFrom = Number(slot.day_from)
                    const dayTo = Number(slot.day_to)
                    const isInRange =
                        dayFrom <= dayTo
                            ? dayOfWeek >= dayFrom && dayOfWeek <= dayTo
                            : dayOfWeek >= dayFrom || dayOfWeek <= dayTo

                    if (!isInRange) return null

                    const fromDay = t(DAYS_OF_WEEK[dayFrom])
                    const toDay = t(DAYS_OF_WEEK[dayTo])
                    const timeFrom = slot.time_from?.slice(0, 5) || '00:00'
                    const timeTo = slot.time_to?.slice(0, 5) || '24:00'

                    return {
                        label: `${fromDay}-${toDay} ${timeFrom}-${timeTo}`,
                        value: index,
                        time_from: timeFrom,
                        time_to: timeTo,
                    }
                })
                .filter(Boolean) as {
                label: string
                value: number
                time_from: string
                time_to: string
            }[]

            // If no slots match the day, return empty - rotation not active on this day
            if (slots.length === 0) {
                return { availableSlots: [], hasNoSlotsForDate: true }
            }
            return { availableSlots: slots, hasNoSlotsForDate: false }
        }

        return { availableSlots: [fullDayOption], hasNoSlotsForDate: false }
    }, [selectedRotation, date, t])

    // Load employee options when modal opens - only active employees
    useEffect(() => {
        const loadEmployees = async () => {
            if (open) {
                try {
                    const options = await getEmployeesOptions(
                        '',
                        undefined,
                        'active' // Only active employees (employee_status = 1)
                    )
                    setEmployeeOptions(options || [])
                } catch (error) {
                    console.error('Failed to load employees:', error)
                    setEmployeeOptions([])
                }
            }
        }
        loadEmployees()
    }, [open])

    useEffect(() => {
        if (open) {
            reset({
                rotation_id:
                    selectedRotationId || rotations[0]?.oncall_rotation_id || 0,
                slot_index: 0,
                employee_id: 0,
                reason: '',
            })
        }
    }, [open, reset, selectedRotationId, rotations])

    const { mutateAsync: doCreate, isPending } = useMutationWithHandler(
        {
            mutationFn: (data: FormData) => {
                const slot =
                    availableSlots[data.slot_index] || availableSlots[0]
                return createOverride({
                    scheduleId,
                    data: {
                        rotation_id: data.rotation_id || undefined,
                        override_date: date,
                        employee_id: data.employee_id,
                        time_from: slot?.time_from || '00:00',
                        time_to: slot?.time_to || '24:00',
                        reason: data.reason,
                    },
                })
            },
        },
        {
            successMessage: t('override_created', 'Override created'),
            reactQueryKeys: [ONCALL_QUERY_KEYS.OVERRIDES],
        }
    )

    const handleCreate = async (formData: FormData) => {
        if (!formData.employee_id) return
        try {
            await doCreate(formData)
            queryClient.invalidateQueries({
                queryKey: [ONCALL_QUERY_KEYS.OVERRIDES, scheduleId],
            })
            onSuccess?.()
            onClose()
        } catch (error) {
            console.error(error)
        }
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            title={t('add_override', 'Add Override')}
            maxWidth="sm"
        >
            <div className="space-y-4">
                <div className="bg-blue-50 p-3 rounded-lg">
                    <span className="text-sm text-blue-700">
                        {t('override_for_date', 'Override for')}:{' '}
                        <strong>{date}</strong>
                    </span>
                </div>

                {rotations.length > 0 && (
                    <FormItem
                        control={control}
                        name="rotation_id"
                        type="select"
                        label={t('rotation', 'Rotation')}
                        options={rotationOptions}
                    />
                )}

                {hasNoSlotsForDate ? (
                    <div className="bg-amber-50 border border-amber-200 p-3 rounded-lg">
                        <span className="text-sm text-amber-700">
                            {t(
                                'no_slots_for_date',
                                'This rotation has no scheduled time slots for this date. Please select a different rotation or date.'
                            )}
                        </span>
                    </div>
                ) : (
                    <FormItem
                        control={control}
                        name="slot_index"
                        type="select"
                        label={t('time_slot', 'Time Slot')}
                        options={availableSlots}
                    />
                )}

                <FormItem
                    control={control}
                    name="employee_id"
                    type="select"
                    label={`${t('employee', 'Employee')} *`}
                    options={employeeOptions}
                />

                <FormItem
                    control={control}
                    name="reason"
                    type="textarea"
                    label={t('reason', 'Reason')}
                    placeholder={t(
                        'override_reason_placeholder',
                        'e.g., Covering for sick leave'
                    )}
                />
            </div>

            <div className="flex justify-end gap-4 mt-6">
                <Button variant="outlined" onClick={onClose}>
                    {t('cancel', 'Cancel')}
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit(handleCreate)}
                    loading={isPending}
                    disabled={isSubmitting || isPending || hasNoSlotsForDate}
                >
                    {t('add_override', 'Add Override')}
                </Button>
            </div>
        </Dialog>
    )
}

export default CreateOverrideModal
